import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ICONS } from '../components/icons/registry'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const ManagedITServicesDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  const services = [
    {
      id: '247-monitoring-support',
      title: '24/7 IT Monitoring & Support',
      description: 'Round-the-clock monitoring of your entire IT infrastructure with proactive issue detection and resolution. Our NOC team ensures your systems stay online and performing optimally, preventing downtime before it impacts your business.',
      icon: ICONS.monitoring,
      features: [
        'Real-time Infrastructure Monitoring',
        'Proactive Alert Management & Escalation',
        'Automated Incident Response',
        'Performance Threshold Optimization',
        'Monthly Health Reports & Analytics',
        'Disaster Recovery & Business Continuity'
      ]
    },
    {
      id: 'network-management-security',
      title: 'Network Management & Security',
      description: 'Comprehensive network management and cybersecurity services to protect your Dubai business from threats. We design, implement, and maintain secure network architectures with enterprise-grade firewall and endpoint protection.',
      icon: ICONS.networkSecurity,
      features: [
        'Firewall & Network Security Management',
        'Endpoint Detection & Response (EDR)',
        'VPN & Secure Remote Access',
        'Network Performance Optimization',
        'Vulnerability Scanning & Patching',
        'Security Compliance & Auditing'
      ]
    },
    {
      id: 'cloud-infrastructure-management',
      title: 'Cloud Infrastructure Management',
      description: 'Expert management of your cloud environments across AWS, Azure, and GCP. We handle provisioning, scaling, cost optimization, and security so your team can focus on core business objectives.',
      icon: ICONS.cloud,
      features: [
        'Cloud Resource Provisioning & Scaling',
        'Cost Optimization & Budget Management',
        'Cloud Security & Access Control',
        'Backup & Disaster Recovery',
        'Multi-cloud & Hybrid Management',
        'Cloud Migration & Modernization'
      ]
    },
    {
      id: 'it-helpdesk-end-user-support',
      title: 'IT Helpdesk & End-User Support',
      description: 'Responsive IT helpdesk services that keep your employees productive. Our support team handles everything from password resets to complex technical issues with defined SLAs and rapid response times.',
      icon: ICONS.support,
      features: [
        'Tiered Support (L1/L2/L3)',
        'Remote & On-site Assistance',
        'User Onboarding & Offboarding',
        'Software Installation & Configuration',
        'Hardware Troubleshooting & Procurement',
        'Knowledge Base & Self-service Portal'
      ]
    }
  ]

  const tools = [
    { name: 'Datadog', category: 'Monitoring' },
    { name: 'PagerDuty', category: 'Incident Mgmt' },
    { name: 'Nagios', category: 'Monitoring' },
    { name: 'Zabbix', category: 'Monitoring' },
    { name: 'ConnectWise', category: 'RMM' },
    { name: 'Datto', category: 'Backup' },
    { name: 'SentinelOne', category: 'Security' },
    { name: 'CrowdStrike', category: 'Security' },
    { name: 'Meraki', category: 'Networking' },
    { name: 'Ubiquiti', category: 'Networking' },
    { name: 'Veeam', category: 'Backup' },
    { name: 'Acronis', category: 'Backup' }
  ]

  const benefits = [
    {
      title: 'Monitoring & Support',
      description: 'Round-the-clock infrastructure monitoring and incident response to keep your business running.',
      stat: '24/7'
    },
    {
      title: 'Average Response Time',
      description: 'Rapid response to critical issues with defined SLAs and escalation procedures.',
      stat: '15min'
    },
    {
      title: 'Uptime SLA',
      description: 'Enterprise-grade reliability with guaranteed uptime for your critical business systems.',
      stat: '99.99%'
    },
    {
      title: 'IT Cost Reduction',
      description: 'Reduce operational IT costs through efficient management, automation, and proactive maintenance.',
      stat: '40%'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const faqs = [
    {
      question: 'What are managed IT services?',
      answer: 'Managed IT services involve outsourcing the responsibility for maintaining and managing your IT infrastructure and systems to a third-party provider known as a Managed Service Provider (MSP). This includes 24/7 monitoring, helpdesk support, network and server management, cybersecurity, cloud infrastructure management, backup and disaster recovery, and regular maintenance. Instead of hiring a full in-house IT team, you get enterprise-grade IT management at a predictable monthly cost.'
    },
    {
      question: 'How much do managed IT services cost in Dubai?',
      answer: 'Managed IT services in Dubai are typically priced per user or per device on a monthly basis. Per-user pricing ranges from AED 200 to AED 800 per month depending on the level of support and services included. Small businesses with 10 to 25 users can expect to pay AED 5,000 to AED 15,000 per month. Mid-sized companies with 50 to 200 users typically pay AED 15,000 to AED 50,000 per month. Enterprise packages with 24/7 support and advanced security start from AED 50,000 per month.'
    },
    {
      question: 'What is included in your managed IT support packages?',
      answer: 'Our managed IT support packages include 24/7 infrastructure monitoring with proactive alerting, tiered helpdesk support (L1/L2/L3), network and server management, endpoint security and antivirus management, backup and disaster recovery, patch management and software updates, vendor management, monthly performance reports, and a dedicated account manager. We offer Standard, Professional, and Enterprise tiers to match different business needs and budgets.'
    },
    {
      question: 'How quickly do you respond to IT issues?',
      answer: 'Our response times are defined by SLA and issue severity. For critical issues affecting business operations (P1), we guarantee a 15-minute response time with 24/7 availability. High-priority issues (P2) receive a response within 1 hour. Medium-priority issues (P3) are addressed within 4 hours, and low-priority requests (P4) within 8 business hours. Our average resolution time for critical issues is under 2 hours.'
    },
    {
      question: 'What is the difference between managed IT services and IT outsourcing?',
      answer: 'Traditional IT outsourcing is typically reactive, meaning you call for help when something breaks and pay per incident. Managed IT services are proactive, with continuous monitoring, regular maintenance, and issue prevention. MSPs use a fixed monthly fee model, making costs predictable. Managed services also include strategic IT planning, security management, and regular reporting, making them a more comprehensive and cost-effective approach for most Dubai businesses.'
    },
    {
      question: 'Can you manage both on-premise and cloud infrastructure?',
      answer: 'Yes, we manage hybrid IT environments that include both on-premise infrastructure and cloud resources across AWS, Azure, and GCP. This includes physical server management, virtual machine administration, cloud resource provisioning and scaling, network connectivity between on-premise and cloud environments, unified monitoring and alerting, and consolidated backup and disaster recovery across all environments.'
    },
    {
      question: 'Do you provide cybersecurity as part of managed IT services?',
      answer: 'Yes, cybersecurity is a core component of our managed IT services. Every package includes endpoint protection, firewall management, vulnerability scanning and patching, and security monitoring. Our Professional and Enterprise tiers add advanced capabilities including EDR (Endpoint Detection and Response), SIEM monitoring, security awareness training, incident response planning, and compliance support for standards like ISO 27001, SOC 2, and NESA.'
    },
    {
      question: 'How do managed IT services help reduce costs?',
      answer: 'Managed IT services reduce costs in several ways. You avoid the expense of hiring, training, and retaining full-time IT staff. Proactive monitoring prevents costly downtime which averages AED 50,000 to AED 200,000 per hour for Dubai businesses. Automated patching and maintenance reduce security breach risks. Cloud cost optimization eliminates overspending on unused resources. Our clients typically see a 30 to 40 percent reduction in total IT operational costs compared to managing everything in-house.'
    }
  ]

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)
  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'Managed IT Services Dubai', path: '/managed-it-services-dubai' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="Managed IT Services Dubai | IT Support & Managed Service Provider UAE"
        description="Professional managed IT services in Dubai. We provide 24/7 monitoring, network security, cloud management, and IT helpdesk support. Your trusted managed service provider in the UAE."
        keywords={[
          'managed it services dubai',
          'managed service provider dubai',
          'it support dubai',
          'it outsourcing dubai',
          'it managed services uae',
          'it monitoring dubai',
          'network management dubai',
          'cloud management dubai',
          'it helpdesk dubai',
          'msp dubai',
          'it infrastructure management uae',
          'remote it support dubai'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              Managed IT Services
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Managed IT Services in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Proactive IT management and support for Dubai businesses: monitoring, maintenance,
              security, and helpdesk services that keep your operations running smoothly while
              reducing costs and complexity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto">
                Schedule IT Consultation
              </CalendlyButton>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits/Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {benefit.stat}
                </div>
                <div className="text-lg font-semibold text-white mb-1">
                  {benefit.title}
                </div>
                <div className="text-primary-100 text-sm">
                  {benefit.description}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our Managed IT Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Comprehensive IT management solutions designed for Dubai businesses looking to
                outsource their IT operations and focus on growth.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                        <Icon className="w-full h-full" darkTreatment="none" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Tools & Technologies We Use
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We leverage industry-leading IT management tools to deliver reliable, secure managed services.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {tools.map((tool, index) => (
                <div
                  key={index}
                  className="px-6 py-3 bg-white dark:bg-gray-700 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{tool.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{tool.category}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about managed IT services in Dubai answered by our team.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-link to other services */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Related Services
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Explore our other specialized technology services for Dubai businesses.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/dubai-services"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                Dubai Services
              </Link>
              <Link
                to="/it-companies-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                IT Companies Dubai
              </Link>
              <Link
                to="/aws-cloud-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                AWS Cloud Consulting
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Outsource Your IT Management?"
        description="Let's discuss how managed IT services can reduce your operational burden and help your Dubai business achieve greater efficiency, security, and uptime."
        primary={{ kind: 'calendly', label: 'Schedule Free Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact' }}
      />
    </div>
  )
}

export default ManagedITServicesDubai
